import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Routes from "../../../routes";
import { getExtra10Outlet, getIsLoadingOutlets } from "../../../store/extra10outlets/selectors";
import useContent from "../../../hooks/useContent";
import Header from "../../common/header/Header";
import Tabs, { ITab } from "../../common/tabs/Tabs";
import Info from "./ComponentInfo";
import { getComponentInfo } from "../../../store/components/actions";
import { getCurrentComponent, getIsLoadingComponent } from "../../../store/components/selectors";
import { LoadingIcon } from "../../common/data_table/dataTable.styles";
import { getExtra10OutletInfo } from "../../../store/extra10outlets/actions";
import HealthMonitor from "./ComponentHealthMonitor";
import Configuration from "./extra10/configuration/Configuration";
import useConfigurationText from "./extra10/configuration/useConfigurationText";
import { EComponentType } from "../../../store/components/types";

interface IProps {
  outletId: string;
  componentId: string;
}

const ComponentDetails: React.FC<IProps> = ({ outletId, componentId }) => {
  const { outletDetails: content, common } = useContent();
  const configurationText = useConfigurationText();
  const dispatch = useDispatch();
  const outlet = useSelector(getExtra10Outlet(outletId));
  const component = useSelector(getCurrentComponent());
  const loadingOutlet = useSelector(getIsLoadingOutlets);
  const loadingComponent = useSelector(getIsLoadingComponent);

  const type = (component?.type?.[0].toLocaleLowerCase() || "") + component?.type?.slice(1);
  useEffect(() => {
    dispatch(getExtra10OutletInfo(outletId));
    dispatch(getComponentInfo(componentId));
  }, [dispatch, outletId, componentId]);

  // TODO: Switch to another content based on component.type
  const tabs = useMemo<ITab[]>(() => {
    const tabsTemp = [
      {
        content: <Info country={outlet?.locationData?.country} />,
        label: common.common_info,
        route: Routes.extra10OutletComponentDetailsInfo
          .replace(":id", outletId)
          .replace(":cid", componentId)
      },
      {
        content: <HealthMonitor componentId={componentId} />,
        label: common.common_health_monitor,
        route: Routes.extra10OutletComponentDetailsHealthMonitor
          .replace(":id", outletId)
          .replace(":cid", componentId)
      }
    ];

    // Remote configuration (probe offsets, fail mode, halo, keg volume) exists for X10 units only.
    if (component?.type === EComponentType.X10) {
      tabsTemp.push({
        content: <Configuration componentId={componentId} />,
        label: configurationText("tab", "Configuration"),
        route: Routes.extra10OutletComponentDetailsConfiguration
          .replace(":id", outletId)
          .replace(":cid", componentId)
      });
    }

    return tabsTemp;
  }, [outletId, outlet, type, componentId, common, component?.type, configurationText]);

  return loadingOutlet || loadingComponent ? (
    <div className="w-full flex items-center justify-center">
      <LoadingIcon />
    </div>
  ) : (
    <div>
      <Header
        title={component ? component.typeContent : ""}
        breadcrumbs={[
          { link: Routes.extra10Outlets, name: content.extra10outlet_details_back_text },
          {
            link: Routes.extra10OutletDetails.replace(":id", outletId),
            name: outlet?.name || ""
          }
        ]}
      />
      <div style={{ overflow: "auto", flex: 1 }}>
        {outlet ? (
          component ? (
            <Tabs tabs={tabs} />
          ) : (
            <div>{content.outlet_component_not_found.replace("%ID%", componentId)}</div>
          )
        ) : (
          <div>{content.outlet_details_not_found.replace("%ID%", outletId)}</div>
        )}
      </div>
    </div>
  );
};

export default ComponentDetails;
